import React, { useCallback, useEffect, useState } from 'react';
import { Wallet } from 'lucide-react';
import Skeleton from '../../../components/common/Skeleton';
import NoDataFound from '../../../components/common/NoDataFound';
import Toast, { type ToastMessage } from '../../../components/common/Toast';
import { myBalances, listLeaveTypes } from '../../../services/hrService';
import { LeaveBalance, LeaveType } from '../../../services/hrTypes';

const MyLeaveBalancesBody: React.FC = () => {
  const [balances, setBalances] = useState<LeaveBalance[]>([]);
  const [types, setTypes] = useState<LeaveType[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<ToastMessage | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [b, t] = await Promise.all([myBalances(), listLeaveTypes()]);
      setBalances(b);
      setTypes(t);
    } catch (err: any) {
      setToast({ type: 'error', message: err.response?.data?.detail || err.message || 'Failed to load balances' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const typeName = (id: number) => types.find((t) => t.id === id)?.name || `Type #${id}`;
  const available = (b: LeaveBalance) => b.allotted - b.used - b.pending;

  return (
    <div className="netbody-card flex flex-col h-full" data-testid="PNL_identity_leave_balances">
      <div className="netbody-header">
        <span style={{ fontWeight: 600, fontSize: '0.9375rem', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Wallet size={16} /> My Leave Balances
        </span>
      </div>
      <div className="netbody-content">
        <div className="scroll-container-nofilters flex-1 overflow-hidden">
          <div className="table-list">
            {loading ? (
              <table className="table-list-table">
                <tbody>
                  <Skeleton.TableRows cols={['sm', 'xs', 'xs', 'xs', 'xs']} />
                </tbody>
              </table>
            ) : balances.length === 0 ? (
              <NoDataFound title="No Leave Balances" message="No leave balances have been allotted to you yet." />
            ) : (
              <div className="table-list-scroll">
                <table className="table-list-table">
                  <thead>
                    <tr>
                      <th className="table-list-th table-list-cell--sno">S.No</th>
                      <th className="table-list-th">Leave Type</th>
                      <th className="table-list-th">Allotted</th>
                      <th className="table-list-th">Used</th>
                      <th className="table-list-th">Pending</th>
                      <th className="table-list-th">Available</th>
                    </tr>
                  </thead>
                  <tbody aria-live="polite">
                    {balances.map((b, idx) => (
                      <tr key={b.id} className="table-list-row" data-testid={`ROW_identity_balance_${b.id}`}>
                        <td className="table-list-cell table-list-cell--sno">{idx + 1}</td>
                        <td className="table-list-cell"><span className="table-list-cell-title">{typeName(b.leave_type_id)}</span></td>
                        <td className="table-list-cell">{b.allotted}</td>
                        <td className="table-list-cell">{b.used}</td>
                        <td className="table-list-cell">{b.pending}</td>
                        <td className="table-list-cell">{available(b)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
};

export default MyLeaveBalancesBody;
